import React, { Suspense, useEffect, useMemo } from 'react'
import { Route, Routes, useLocation, useMatch } from 'react-router-dom'
import classnames from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import dayjs from 'dayjs'
import LocalizedFormat from 'dayjs/plugin/localizedFormat'
import localeData from 'dayjs/plugin/localeData'
import Page from './Page'
import { setAppSettings, setPages } from '../store/slices/appSlice'
import Logout from '../views/Auth/Logout/Logout'
import Header from './Header'
import Sidebar from './Sidebar'
import useUser from '../hooks/useUser'
import SessionLogout from '../SessionLogout'

import('dayjs/locale/da')
import('dayjs/locale/en')
import('dayjs/locale/sv')
import('dayjs/locale/nb')

dayjs.extend(LocalizedFormat)
dayjs.extend(localeData)

const Layout = () => {
  const loginMatch = useMatch('/auth/*')
  const { user, pages, appSettings } = useSelector((state) => state?.app)
  const rehydrated = useSelector((state) => state?._persist?.rehydrated)
  const dispatch = useDispatch()
  const location = useLocation()

  const { refetch } = useUser()

  useEffect(() => {
    if (window.gtag && user && sessionStorage.getItem('login') !== 'true') {
      sessionStorage.setItem('login', true)
      gtag('event', 'login')
    }
  }, [user])

  // TODO: Why is this here? Seems OK to remove but doing so needs thorough testing
  useEffect(() => {
    !loginMatch && refetch()
  }, [location.pathname, loginMatch, refetch])

  // TODO: move these calls into two separate custom hooks to avoid refetching each time the user navigates
  useEffect(() => {
    axios
      .get('/api/pages')
      .then((response) => response.data)
      .then((routeResponse) => {
        if (!_.isEqual(pages, routeResponse)) {
          dispatch(setPages(routeResponse))
        }
      })
    axios.get('/api/appsettings').then((res) => {
      let { locale } = res.data

      switch (locale) {
        case 'dk':
          locale = 'da' // Dayjs sees "da" as danish while "dk" is the standard
          break
        case 'no':
          locale = 'nb' // Dayjs has noth nynorsk (nn) and bokmål (nb). Set Dayjs locale to "nb" if "no" in appSettings
          break
        default:
          break
      }

      dayjs.locale(locale)
      if (!_.isEqual(appSettings, res.data)) {
        dispatch(setAppSettings(res.data))
      }
    })
  }, [dispatch, user, appSettings, pages])

  const routes = useMemo(
    () =>
      _.map(pages, (page, key) => {
        const Element = page.viewPath && React.lazy(() => import(`../views/${page.viewPath}`))
        return (
          <Route
            key={key}
            path={page.url}
            element={
              <Page page={page} noPadding={_.includes(page.url, 'auth')}>
                {Element && _.includes(page.url, 'auth') ? (
                  <Element />
                ) : Element && !_.includes(page.url, 'auth') ? (
                  <SessionLogout>
                    <Element />
                  </SessionLogout>
                ) : (
                  <div>Kan inte hitta den här sidan</div>
                )}
              </Page>
            }
          />
        )
      }),
    [pages],
  )

  return (
    appSettings &&
    pages &&
    rehydrated && (
      <div
        id="layout"
        className={classnames({
          'has-header': !!loginMatch,
          'has-sidebar': !loginMatch,
        })}
      >
        {user && appSettings && (
          <Header pages={pages} location={location}>
            {!loginMatch && <Sidebar />}
          </Header>
        )}
        <div id="main">
          <Suspense
            fallback={
              <div className="suspense-loader">
                <div className="spinner-grow text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            }
          >
            <Routes>
              {routes}
              <Route path="/auth/logout" element={<Logout />} />
              {/* {!user && rehydrated && <Route path="/" element={<Navigate to="/auth/login" />} />} */}
            </Routes>
          </Suspense>
        </div>
      </div>
    )
  )
}

export default Layout
